import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { ArrowRight, ShieldCheck, Warning } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const invalidLink = useMemo(() => !token || token.length < 16, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters");
    if (pw !== pw2) return setErr("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#F9F7F4]">
      <div className="flex-1 grid place-items-center p-8">
        <div className="w-full max-w-md card !p-10" data-testid="reset-password-card">
          {invalidLink ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-14 h-14 rounded-full bg-[#FBE3E3] grid place-items-center mx-auto">
                <Warning size={28} weight="fill" className="text-[#C93B3B]" />
              </div>
              <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Invalid link</h1>
              <p className="text-sm text-[#4F5D4F] mt-3">This reset link is missing or malformed. Please request a new one.</p>
              <Link to="/forgot-password" className="btn-primary mt-6" data-testid="request-new-link">Request a new link</Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-[#E2F3EB] grid place-items-center mx-auto">
                <ShieldCheck size={28} weight="fill" className="text-[#2D7A5D]" />
              </div>
              <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Password updated</h1>
              <p className="text-sm text-[#4F5D4F] mt-3">You can now sign in with your new password. Redirecting…</p>
              <Link to="/login" className="btn-primary mt-6" data-testid="go-to-login">Go to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <div className="overline mb-3">Set a new password</div>
              <h1 className="font-display text-3xl font-medium tracking-tight text-[#132A13]">Choose a new password</h1>
              <p className="text-sm text-[#4F5D4F] mt-2">Pick something you&apos;ll remember. Minimum 6 characters.</p>

              <div className="mt-7 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">New password</label>
                  <input
                    type="password"
                    className="input mt-1"
                    required
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="•••••••"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Confirm password</label>
                  <input
                    type="password"
                    className="input mt-1"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="•••••••"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                {err && <p className="text-sm text-[#C93B3B]" data-testid="reset-error">{err}</p>}
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit-button">
                  {loading ? "Updating…" : <>Update password <ArrowRight size={16} weight="bold" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
